import React, { useMemo, useState } from "react";

function ChevronDown({ open }) {
  return (
    <svg
      className={[
        "h-5 w-5 flex-shrink-0 text-muted transition-transform duration-200",
        open ? "rotate-180" : "rotate-0",
      ].join(" ")}
      viewBox="0 0 24 24"
      fill="none"
      aria-hidden="true"
    >
      <path
        d="M6.5 9.5 12 15l5.5-5.5"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function slugify(input) {
  return String(input || "")
    .toLowerCase()
    .trim()
    .replace(/['"]/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 40);
}

function normalizeItems(items) {
  const seen = new Set();

  return (items || []).map((it, idx) => {
    const baseFromQ = slugify(it.q);
    const base = it.id ? slugify(it.id) : baseFromQ || `item-${idx}`;

    let id = `faq-${base}-${idx}`;
    if (seen.has(id)) {
      let n = 2;
      while (seen.has(`${id}-${n}`)) n += 1;
      id = `${id}-${n}`;
    }
    seen.add(id);

    return {
      id,
      q: it.q,
      a: it.a,
    };
  });
}

export default function FaqAccordion({
  items,
  defaultOpenIndex = 0,
  allowMultiple = true,
  className = "",
}) {
  const normalized = useMemo(() => normalizeItems(items), [items]);

  const [openSet, setOpenSet] = useState(() => {
    const s = new Set();
    if (
      typeof defaultOpenIndex === "number" &&
      defaultOpenIndex >= 0 &&
      defaultOpenIndex < normalized.length
    ) {
      s.add(normalized[defaultOpenIndex].id);
    }
    return s;
  });

  const isOpen = (id) => openSet.has(id);

  const toggle = (id) => {
    setOpenSet((prev) => {
      const next = new Set(prev);
      const currentlyOpen = next.has(id);

      if (!allowMultiple) {
        next.clear();
        if (!currentlyOpen) next.add(id);
        return next;
      }

      if (currentlyOpen) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const expandAll = () => {
    if (!allowMultiple) return;
    setOpenSet(new Set(normalized.map((x) => x.id)));
  };

  const collapseAll = () => setOpenSet(new Set());

  return (
    <div className={className}>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="text-sm text-muted">{normalized.length} questions</div>

        {allowMultiple && (
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={expandAll}
              className="rounded-xl px-3 py-2 text-xs font-semibold text-ink/80 ring-1 ring-ink/10 transition hover:bg-bg1 focus:outline-none focus:ring-4 focus:ring-hint/30"
            >
              Expand all
            </button>
            <button
              type="button"
              onClick={collapseAll}
              className="rounded-xl px-3 py-2 text-xs font-semibold text-ink/80 ring-1 ring-ink/10 transition hover:bg-bg1 focus:outline-none focus:ring-4 focus:ring-hint/30"
            >
              Collapse all
            </button>
          </div>
        )}
      </div>

      <div className="mt-6 grid grid-cols-1 gap-3">
        {normalized.map((item) => {
          const open = isOpen(item.id);
          const panelId = `${item.id}-panel`;
          const buttonId = `${item.id}-button`;

          return (
            <div
              key={item.id}
              className="rounded-2xl border border-ink/8 bg-white shadow-card"
            >
              <button
                id={buttonId}
                type="button"
                aria-expanded={open}
                aria-controls={panelId}
                onClick={() => toggle(item.id)}
                className="flex w-full items-center justify-between gap-4 rounded-2xl px-5 py-4 text-left transition hover:bg-bg1/70 focus:outline-none focus:ring-4 focus:ring-hint/25"
              >
                <div className="min-w-0 text-sm font-semibold text-ink">{item.q}</div>
                <div className="flex items-center gap-2">
                  <span className="hidden text-[11px] text-muted sm:inline">
                    {open ? "Hide" : "Show"}
                  </span>
                  <ChevronDown open={open} />
                </div>
              </button>

              <div
                id={panelId}
                role="region"
                aria-labelledby={buttonId}
                className={[
                  "grid transition-[grid-template-rows] duration-300 ease-out",
                  open ? "grid-rows-[1fr]" : "grid-rows-[0fr]",
                ].join(" ")}
              >
                <div className="overflow-hidden">
                  <div className="px-5 pb-5 text-sm leading-relaxed text-muted">
                    {typeof item.a === "string" ? <p>{item.a}</p> : item.a}
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      <p className="mt-5 text-xs text-muted">
        Not financial advice. Educational purposes only.
      </p>
    </div>
  );
}
